import { useEffect, useState } from 'react';
import { useT } from '../i18n/useT';
import {
  chooseSyncFolder,
  nativeVault,
  getFolderSyncState,
  runFolderSync,
  stopFolderSync,
  supportsFolderSync,
  type FolderSyncState,
} from '../lib/folderSync';
import { askConfirm } from '../lib/dialogs';
import { syncStatusLine } from './syncStatus';

/**
 * Settings for syncing the library with a folder on disk.
 *
 * Choosing a folder asks first, and says what will happen, because unlike a
 * backup this reads the folder back: its files become documents, and later edits
 * to them change documents.
 */
export function FolderSyncSettings() {
  const t = useT();
  const [state, setState] = useState<FolderSyncState | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void getFolderSyncState().then(setState);
  }, []);

  const run = async (work: () => Promise<FolderSyncState | null>) => {
    setBusy(true);
    try {
      const next = await work();
      setState(next ?? (await getFolderSyncState()));
    } finally {
      setBusy(false);
    }
  };

  // In a shell the vault is not a setting: notes are always files in the app's
  // own folder, and the storage row above says where. Nothing to choose here.
  if (nativeVault()) return null;

  if (!supportsFolderSync()) {
    return (
      <div className="mb-setting mb-setting-block">
        <label>{t('folderSync.title')}</label>
        <p className="mb-modal-note">{t('folderSync.unsupported')}</p>
      </div>
    );
  }

  // A repository is the other half of this setting, and has its own section.
  const onFolder = state?.target !== 'github';
  const status = syncStatusLine(onFolder ? state : null, t);

  return (
    <div className="mb-setting mb-setting-block">
      <label>{t('folderSync.title')}</label>
      <div className="mb-backup">
        <p className="mb-modal-note">{t('folderSync.hint')}</p>
        <div className="mb-backup-row">
          <span className="mb-backup-folder">
            {state?.enabled && onFolder && state.folderName
              ? t('folderSync.folderIs', { name: state.folderName })
              : t('folderSync.noFolder')}
          </span>
          <button
            className="mb-btn"
            disabled={busy}
            onClick={() =>
              void run(async () => {
                const ok = await askConfirm({
                  title: t('folderSync.confirmTitle'),
                  body: t('folderSync.confirmBody'),
                  confirmLabel: t('folderSync.choose'),
                });
                if (!ok) return null;
                const chosen = await chooseSyncFolder();
                return chosen ? runFolderSync(true) : null;
              })
            }
          >
            {state?.enabled && onFolder ? t('folderSync.change') : t('folderSync.choose')}
          </button>
        </div>
        {state?.enabled && onFolder && (
          <div className="mb-backup-row">
            <button
              className="mb-btn"
              disabled={busy}
              onClick={() => void run(() => runFolderSync(true))}
            >
              {state.lastProblem === 'permission' ? t('folderSync.grant') : t('folderSync.now')}
            </button>
            <button className="mb-btn" disabled={busy} onClick={() => void run(stopFolderSync)}>
              {t('folderSync.stop')}
            </button>
          </div>
        )}
        <p className="mb-backup-msg" role="status">
          {status}
        </p>
      </div>
    </div>
  );
}
